import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { X, Tag } from "lucide-react";

const MAX = 25;
const MAXLEN = 40;

// Free discovery-tag chip input. Typeahead SUGGESTS existing yard keywords (to curb
// typos / near-dupes) but any brand-new keyword can always be added — NOT a managed
// list. Keywords are per-part; never inherited.
export function KeywordChips({ value = [], onChange, testid = "keywords" }) {
  const [text, setText] = useState("");
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const tags = Array.isArray(value) ? value : [];

  useEffect(() => {
    const q = text.trim();
    if (!q) { setSugs([]); return; }
    let alive = true;
    const t = setTimeout(() => {
      api.get(`/parts/keywords/suggest`, { params: { q } })
        .then(({ data }) => { if (alive) setSugs((data.keywords || []).filter((k) => !tags.some((x) => x.toLowerCase() === k.toLowerCase()))); })
        .catch(() => {});
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [text]); // eslint-disable-line

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const add = (raw) => {
    const s = String(raw || "").trim().slice(0, MAXLEN);
    if (!s) return;
    if (tags.some((x) => x.toLowerCase() === s.toLowerCase())) { setText(""); return; }
    if (tags.length >= MAX) return;
    onChange([...tags, s]);
    setText(""); setSugs([]); setOpen(false);
  };
  const remove = (i) => onChange(tags.filter((_, idx) => idx !== i));

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(text); }
    else if (e.key === "Backspace" && !text && tags.length) remove(tags.length - 1);
  };

  return (
    <div ref={boxRef} className="relative" data-testid={`${testid}-wrap`}>
      <div className="flex flex-wrap gap-1.5 rounded-lg border border-slate-200 bg-white p-2 min-h-[44px] focus-within:border-[#E0781A] transition-colors">
        {tags.map((t, i) => (
          <span key={`${t}-${i}`} data-testid={`${testid}-chip-${i}`}
            className="inline-flex items-center gap-1 rounded-full bg-[#FBEFE2] text-[#B45309] text-xs font-semibold px-2.5 py-1">
            <Tag className="w-3 h-3" />{t}
            <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => remove(i)}
              className="ml-0.5 hover:text-[#7c3a06]"><X className="w-3 h-3" /></button>
          </span>
        ))}
        <input
          data-testid={`${testid}-input`}
          value={text}
          disabled={tags.length >= MAX}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={tags.length >= MAX ? `Max ${MAX} keywords` : (tags.length ? "Add another…" : "e.g. Ford, 2005ish, dually")}
          className="flex-1 min-w-[120px] bg-transparent outline-none text-sm px-1"
        />
      </div>
      <p className="text-[10px] text-slate-400 mt-1">{tags.length}/{MAX} · Enter or comma to add · free tags, findable in search</p>
      {open && text.trim() && (sugs.length > 0 || true) && (
        <div data-testid={`${testid}-suggest`} className="absolute z-50 left-0 right-0 mt-1 rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden">
          {sugs.slice(0, 8).map((s) => (
            <button key={s} type="button" data-testid={`${testid}-suggest-${s}`} onClick={() => add(s)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
              <Tag className="w-3 h-3 text-slate-400" />{s}
            </button>
          ))}
          <button type="button" data-testid={`${testid}-add-new`} onClick={() => add(text)}
            className="w-full text-left px-3 py-2 text-sm bg-slate-50 hover:bg-slate-100 border-t border-slate-100 flex items-center gap-2 font-medium text-[#B45309]">
            <Tag className="w-3 h-3" />Add “{text.trim()}”
          </button>
        </div>
      )}
    </div>
  );
}
